import { getPageUrl } from '@/utils/helpers';
import {
  isAccordionGroup,
  isEsTabbedContent,
  isHorizontalButtonSet,
  isMyBusiness,
  isPowerApps,
  isTabbedDocumentPicker,
  isTabbedList,
  isToggleMenu,
  isWelcomeMessage,
  resolveAccordionGroup,
  resolveEsTabbedContent,
  resolveHorizontalButtonSet,
  resolveMyBusiness,
  resolverPowerApps,
  resolveTabbedDocumentPicker,
  resolveTabbedList,
  resolveToggleMenu,
  resolveWelcomeMessage,
  isRichTextContent,
  resolveRichText,
} from './content-blocks';
import resolveColorName from './resolveColorName';
import resolveCompound from './resolveCompound';
import resolveDate from './resolveDate';
import resolveDocument from './resolveDocument';
import resolveImage from './resolveImage';
import resolveLink from './resolveLink';
import resolvePerson from './resolvePerson';
import resolveReference from './resolveReference';
import resolveRTC from './resolveRTC';
import resolveString from './resolveString';
import resolveTheme from './resolveTheme';
import {
  isDocumentPicker,
  resolveDocumentPicker,
} from '@/utils/resolvers/content-blocks/resolveDocumentPicker';

const resolveContentBlocks = ({
  rawVal,
  page,
  pageModels,
}: PropsWithCMSProps<ContentBlocksProps>): DetailContentType[] => {
  const result = (rawVal || [])
    .map((raw) => {
      // for conte type supporting
      const { compoundName: cn, contentType, value, $ref, ...props } = raw;
      const compoundName = cn || contentType.replace('genetecweb:', '');

      if (isRichTextContent(compoundName)) return resolveRichText(raw);
      if (isWelcomeMessage(compoundName)) return resolveWelcomeMessage(raw);
      if (isMyBusiness(compoundName)) return resolveMyBusiness(raw, page, pageModels);
      if (isHorizontalButtonSet(compoundName))
        return resolveHorizontalButtonSet(raw, page, pageModels);
      if (isTabbedList(compoundName)) {
        return resolveTabbedList(raw, page, pageModels, props);
      }
      if (isTabbedDocumentPicker(compoundName)) {
        return resolveTabbedDocumentPicker(raw, page, pageModels, props, resolveContentBlocks);
      }
      if (isToggleMenu(compoundName)) {
        return resolveToggleMenu(raw, page, pageModels);
      }
      if (isAccordionGroup(compoundName)) {
        return resolveAccordionGroup(raw, page, pageModels);
      }
      if (isPowerApps(compoundName)) {
        return resolverPowerApps(raw, page, pageModels);
      }

      if (isEsTabbedContent(compoundName)) {
        return resolveEsTabbedContent(raw, page, pageModels);
      }

      if (/^image/i.test(compoundName)) {
        return {
          variant: 'image',
          image: resolveImage({ rawVal: raw, page }),
        };
      }

      if (/^multiButton/i.test(compoundName)) {
        const { multiButtonWrapper = [] } = raw;
        return {
          variant: 'multiLink',
          links: multiButtonWrapper.map((item: any) =>
            resolveLink({ rawVal: item, page, pageModels }),
          ),
        };
      }

      if (/^button/i.test(compoundName)) {
        return {
          variant: 'link',
          link: resolveLink({ rawVal: raw, page, pageModels }),
        };
      }

      if (/^video/i.test(compoundName)) {
        // MODIFIED, PLEASE CONSIDER IT IN THE REFACTOR (@luis.sardon).
        return {
          variant: 'video',
          videoUrl: props.videoUrl,
          fallbackImage: resolveImage({ rawVal: props?.fallbackImage, page }),
        };
      }

      if (/^quote/i.test(compoundName)) {
        return {
          variant: 'quote',
          quote: resolveRTC({ rawVal: props.quote }),
          image: resolveImage({ rawVal: props.imagecompound, page }),
          orientation: props.imageOrientation?.trim(),
          person: resolveRTC({ rawVal: props.person }),
        };
      }

      if (/^promo/i.test(compoundName)) {
        const content = resolveReference<any>({ rawVal: props.ctaContent, page }) || {};

        return {
          variant: 'cta',
          logo: resolveImage({ rawVal: content?.logo, page }),
          detailContent: resolveRTC({ rawVal: content?.detailContent }),
          primaryCTA: resolveLink({ rawVal: content?.primaryCTA, page, pageModels }),
          secondaryCTA: resolveLink({ rawVal: content?.secondaryCTA, page, pageModels }),
          backgroundImage: resolveImage({ rawVal: content?.backgroundImage, page }),
          mobileBackgroundImage: resolveImage({ rawVal: content?.mobileBackgroundImage, page }),
          backgroundColor: resolveColorName({ rawVal: props.themeBackground }),
          nonThemeBackgroundColor: resolveColorName({ rawVal: props.nonThemeBackground }),
          theme: resolveTheme('text-color')({ rawVal: props.textColor }),
          heading: content?.heading,
        };
      }

      if (/^two/i.test(compoundName)) {
        return {
          variant: 'twoColumn',
          title: props.title,
          leftColumn: resolveContentBlocks({ rawVal: props.leftColumn, page, pageModels }),
          rightColumn: resolveContentBlocks({ rawVal: props.rightColumn, page, pageModels }),
          verticalAlignment: props.verticalAlignment,
          columnRatio: props.columnRatio,
        };
      }

      if (/^iconText/i.test(compoundName)) {
        // TODO: Improve by adding type casting that does not depend on importing directly from IconTextGrid
        let iconTextGrid = [];
        if (props.iconTextGrid && props.iconTextGrid.length) {
          iconTextGrid = props.iconTextGrid.map((compound: any) => {
            const { icon, text } = compound;
            const resolvedCompound = {
              icon: resolveImage({ rawVal: icon, page }),
              text: resolveRTC({ rawVal: text }),
            };
            return resolvedCompound;
          });
          iconTextGrid = iconTextGrid.length > 4 ? iconTextGrid.slice(0, 4) : iconTextGrid;
        }
        return {
          variant: 'iconTextGrid',
          leftGroupCta:
            props.leftGroupCta && resolveLink({ rawVal: props.leftGroupCta, page, pageModels }),
          leftGroupCtaStyle: props.leftGroupCtaStyle,
          leftGroupLogo: resolveImage({ rawVal: props.leftGroupLogo, page }),
          leftGroupDescription: resolveRTC({ rawVal: props.leftGroupDescription }),
          iconTextGrid,
        };
      }

      if (/^three/i.test(compoundName)) {
        return {
          variant: 'threeColumn',
          title: props.title,
          isAutoLayout: !!props?.autoLayout,
          leftColumn: resolveContentBlocks({ rawVal: props.leftColumn, page, pageModels }),
          centerColumn: resolveContentBlocks({ rawVal: props.centerColumn, page, pageModels }),
          rightColumn: resolveContentBlocks({ rawVal: props.rightColumn, page, pageModels }),
        };
      }

      if (/^fourFive/i.test(compoundName)) {
        return {
          variant: 'fourFiveColumn',
          title: props.title,
          columns: props.columns.map((column: any) =>
            resolveContentBlocks({ rawVal: column.content, page, pageModels }),
          ),
        };
      }

      if (/^solutionsSummary/i.test(compoundName)) {
        return {
          variant: 'solutionsSummary',
          stats: props.companyStats.map(({ stat: title, statType: pretitle }: any) => ({
            title,
            pretitle,
          })),
          content: props.challenge.map(({ superheading, superheadingTextColor, content }: any) => ({
            superHeading: superheading,
            superHeadingColor: /^blue/i.test(superheadingTextColor) ? 'blue' : 'standard',
            content: resolveRTC({ rawVal: content }),
          })),
        };
      }

      if (/^embed/i.test(compoundName)) {
        return {
          variant: 'embeddedContent',
          content: props.embeddedContent.replace(/(\r\n|\n|\r|\u200b)/gm, ''),
          fullBleed: /bleed$/i.test(props.presentation),
        };
      }

      /* SquareTileCta */
      if (/^squareTile/i.test(compoundName)) {
        const { titleTextColor, smallCta = [], squareCta = [] } = props;

        const columns = Math.min(3, squareCta.length + Math.min(1, smallCta.length));
        const squares = squareCta.map((elm: any) => ({
          type: 'squareCTA',
          content: {
            image: resolveImage({ rawVal: elm.logoIcon, page }),
            title: elm.title,
            description: elm.description,
            link: resolveLink({ rawVal: elm.button, page, pageModels }),
          },
        }));

        const smalls = smallCta.map((elm: any) => ({
          image: resolveImage({ rawVal: elm.logoIcon, page }),
          label: elm.label,
          link: page && getPageUrl({ url: elm.link, page }),
        }));

        const stackedSmalls =
          smalls.length > 0
            ? {
                type: 'column',
                content: smalls,
              }
            : null;

        const titleColor = titleTextColor?.includes('blue') ? 'blue' : undefined;

        const content = [...squares, stackedSmalls].filter((elm) => !!elm);

        return {
          variant: 'squareTileCTA',
          columns,
          content,
          title: props.title,
          titleTextColor: titleColor,
        };
      }

      if (/^card/i.test(compoundName)) {
        const compound = resolveCompound<CardCompound>({ rawVal: raw });

        const {
          documentReference,
          buttonlinkcompound,
          cardAuthor,
          cardCategory,
          cardDescription,
          cardHeading,
          cardImage,
          cardLogo,
          cardPublicationDate,
          cardReadTime,
          customerStoryCardInfo,
          featuredContent,
          url,
          openInNewWindow,
          cardBackgroundImage,
        } = compound;
        const document = resolveDocument({ rawVal: documentReference, page, pageModels }) || {};

        const {
          heading,
          logo,
          image,
          publicationDate,
          url: documentUrl,
          author,
          category,
          shortDescription,
          cardLink,
          cardTextLink,
          cardSecondaryButton,
          cardReadTime: documentCardReadTime,
          cardDescription: cardDescriptionDoc,
          releaseDate,
          cardBackgroundImage: documentCardBackgroundImage,
        } = document;

        const buttonLinkRaw = buttonlinkcompound || cardSecondaryButton || cardTextLink;

        return {
          variant: 'card',
          buttonLink: resolveLink({ rawVal: buttonLinkRaw, page, pageModels }),
          secondaryButtonLink: resolveLink({ rawVal: cardSecondaryButton, page, pageModels }),
          cardAuthor: resolvePerson({ rawVal: cardAuthor || author, page }),
          cardCategory: cardCategory || (category && category[0]) || '',
          cardDescription: cardDescription?.value
            ? resolveRTC({
                rawVal: cardDescription,
              })
            : cardDescriptionDoc,
          cardHeading: cardHeading || heading,
          cardImage: cardImage?.image ? resolveImage({ rawVal: cardImage, page }) : image,
          cardLogo: cardLogo ? resolveImage({ rawVal: cardLogo, page }) : logo,
          cardBackgroundImage: cardBackgroundImage?.image
            ? resolveImage({ rawVal: cardBackgroundImage, page })
            : documentCardBackgroundImage,
          cardPublicationDate:
            releaseDate ||
            (cardPublicationDate ? resolveDate({ rawVal: cardPublicationDate }) : publicationDate),
          cardReadTime: documentCardReadTime || cardReadTime,
          customerStoryCardInfo: resolveRTC({ rawVal: customerStoryCardInfo }),
          featuredContent,
          // cardTextLink is an object that contains 'url' attribute. MIGRATE TO NEW PLACE (REFACTOR) @luis.sardon
          url:
            page && getPageUrl({ url: cardLink || cardTextLink?.url || url || documentUrl, page }),
          openInNewWindow,
        };
      }

      if (/^socialPlatformsCompound/i.test(compoundName)) {
        const { images } = raw || {};
        return {
          variant: 'socialPlatforms',
          images: (images || []).map((image: any) => resolveImage({ rawVal: image, page })),
        };
      }

      if (/^accordionCompound/i.test(compoundName)) {
        const { accordionTitle, isCollapsed, accordionContent } = raw || {};
        return {
          variant: 'accordion',
          isCollapsed,
          title: resolveRTC({ rawVal: { value: accordionTitle.value } }),
          content: resolveRTC({ rawVal: { value: accordionContent.value } }),
        };
      }

      if (isDocumentPicker(compoundName)) {
        return resolveDocumentPicker(raw, page, pageModels);
      }

      if (/^table/i.test(compoundName)) {
        return {
          variant: 'table',
          table: props.table?.value,
        };
      }

      if (/^contentList/i.test(compoundName)) {
        const {
          contentList: contentListRef,
          // containerBackgroundColor,
          cardColor,
          // containerTopSpacing,
          template,
          // hasTextShadow,
          // textColor,
          // containerBottomSpacing,
        } = props;

        const contentListDoc = resolveReference<any>({ rawVal: contentListRef, page }) || {};

        const { listSuperheading, listTitle, contentList } = contentListDoc || {};
        return {
          variant: 'contentList',
          content: {
            listSuperheading,
            listTitle,
            contentList: resolveContentBlocks({ rawVal: contentList, page, pageModels }),
          },
          presentation: {
            // containerBackgroundColor,
            cardColor: resolveColorName({ rawVal: cardColor }),
            // containerTopSpacing,
            template: resolveString({ rawVal: template }),
            // hasTextShadow,
            // textColor,
            // containerBottomSpacing,
          },
        };
      }

      if ($ref) {
        return {
          variant: 'document',
          ...(resolveReference<any>({ rawVal: raw, page }) || {}),
        };
      }

      return (
        value && {
          variant: 'rtc',
          content: resolveRTC({ rawVal: { value } }),
        }
      );
    })
    .filter((el) => Boolean(el)) as DetailContentType[];

  return result;
};

export default resolveContentBlocks;
